// Pure-logic tracker that decides whether the local player can send an AFK
// ping to the remote opponent. Per-game controllers feed it turn transitions
// and ping-send events; per-app UI subscribes for visibility changes.

const DEFAULT_IDLE_THRESHOLD_MS = 15_000;
const DEFAULT_COOLDOWN_MS = 15_000;
const POLL_INTERVAL_MS = 500;

export interface AfkPingTrackerOptions {
  readonly idleThresholdMs?: number;
  readonly cooldownMs?: number;
}

type Listener = () => void;

export class AfkPingTracker {
  private readonly idleThresholdMs: number;
  private readonly cooldownMs: number;
  private opponentTurnStartedAt: number | null = null;
  private lastPingSentAt: number | null = null;
  private listeners: Set<Listener> = new Set();
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private lastNotifiedCanPing: boolean = false;

  constructor(options?: AfkPingTrackerOptions) {
    this.idleThresholdMs = options?.idleThresholdMs ?? DEFAULT_IDLE_THRESHOLD_MS;
    this.cooldownMs = options?.cooldownMs ?? DEFAULT_COOLDOWN_MS;
    this.intervalHandle = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  setOpponentTurn(active: boolean): void {
    if (active) {
      this.opponentTurnStartedAt = Date.now();
    } else {
      this.opponentTurnStartedAt = null;
    }
  }

  markPingSent(): void {
    this.lastPingSentAt = Date.now();
  }

  canPing(): boolean {
    if (this.opponentTurnStartedAt === null) return false;
    const now = Date.now();
    if (now - this.opponentTurnStartedAt < this.idleThresholdMs) return false;
    if (this.lastPingSentAt !== null && now - this.lastPingSentAt < this.cooldownMs) {
      return false;
    }
    return true;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  dispose(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    this.listeners.clear();
  }

  private poll(): void {
    const current = this.canPing();
    if (current === this.lastNotifiedCanPing) return;
    this.lastNotifiedCanPing = current;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
